/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, select, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionSuccess,
  Folder,
  FolderType,
  SearchUtils,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  AssetMediaType,
  ParentalControlSettings,
  HideUnsubscribed,
  LEGACY,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import AppConfig from '../../../services/AppConfig';

export enum ActionType {
  ADD = 'folders/ADD',
}

type Add = ActionSuccess<ActionType.ADD, Folder[]>;
type Actions = Add;

const Actions = {
  add: (payload: Folder[]): Add => ({ type: ActionType.ADD, payload }),
};

// SELECTORS
export const Selectors = {
  getFolders: (
    state: StoreState
  ): {
    [id: number]: Folder;
  } => state.folders,
};

const subscriptionIdComparison = (folder: Folder, subscriptionId: string): boolean =>
  folder.productId === subscriptionId && folder.folderType === FolderType.SUBSCRIPTION;

const collectionIdComparison = (folder: Folder, collectionId: string): boolean =>
  folder.productId === collectionId && folder.folderType === FolderType.BUNDLE;

function* checkCachedFolders(ids: string[], comparison: (folder: Folder, id: string) => boolean): SagaIterator {
  const missingIds: string[] = [];
  const foundFolders: Folder[] = [];

  const foldersMap: ReducerState = yield select(Selectors.getFolders);
  const foldersArray = Object.values(foldersMap);

  let foundFolder: Folder[];
  ids.forEach((id) => {
    foundFolder = foldersArray.filter((folder) => comparison(folder, id));
    if (foundFolder.length) {
      foundFolders.push(...foundFolder);
    } else {
      missingIds.push(id);
    }
  });

  // [...missingIds] - converts Set to Array
  return { missingIds, foundFolders };
}

// SAGAS
function* searchFolders(ksql: string): SagaIterator<Folder[]> {
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  // get the folders related with the subscriptions ids
  const searchedFolders: Folder[] = yield Ducks.Pagination.callCompletedPaginatedApi(
    ksql,
    Constants.DEFAULT_API_PAGE_SIZE,
    AssetsApi.assetSearch,
    ksql,
    filesFormat,
    parentalControlSettings,
    [AssetMediaType.FOLDER],
    LEGACY.KalturaAssetOrderBy.RELEVANCY_DESC,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );

  // update the local reducer
  yield put(Actions.add(searchedFolders));

  yield Ducks.Pagination.clearPaginatedApi(ksql);

  return searchedFolders;
}

function* getSubscriptionsFolders(subscriptionIds: string[]): SagaIterator<Folder[]> {
  const { missingIds, foundFolders: cachedFolders } = yield call(
    checkCachedFolders,
    subscriptionIds,
    subscriptionIdComparison
  );

  // all folders are available in reducer
  if (missingIds.length === 0) {
    return cachedFolders;
  }

  const ksql = SearchUtils.createFoldersFilter(missingIds, 'subscription_id');

  if (ksql === '') {
    return [];
  }

  // get the folders related with the subscriptions ids
  const searchedFolders: Folder[] = yield call(searchFolders, ksql);

  return [...cachedFolders, ...searchedFolders];
}

function* getCollectionsFolders(collectionIds: string[]): SagaIterator<Folder[]> {
  const { missingIds, foundFolders: cachedFolders } = yield call(
    checkCachedFolders,
    collectionIds,
    collectionIdComparison
  );

  // all folders are available in reducer
  if (missingIds.length === 0) {
    return cachedFolders;
  }

  const ksql = SearchUtils.createFoldersFilter(missingIds, 'collectionId');

  if (ksql === '') {
    return [];
  }

  // get the folders related with the subscriptions ids
  const searchedFolders: Folder[] = yield call(searchFolders, ksql);

  return [...cachedFolders, ...searchedFolders];
}

export const SagaFunctions = {
  getSubscriptionsFolders,
  getCollectionsFolders,
};

// Folders REDUCER
export type ReducerState = {
  [id: number]: Folder;
};

export default function folders(state: ReducerState = {}, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.ADD: {
      const foldersMap: ReducerState = {};
      action.payload.forEach((folder) => {
        foldersMap[folder.id] = folder;
      });
      return { ...state, ...foldersMap };
    }
    default:
      return state;
  }
}
